import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import {
  ArrowLeft, ShieldCheck, BuildingOffice, MapPin, ChartLineUp,
  CheckCircle, FileText, Lock, ArrowRight, Pulse, Recycle,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Major Indian state capitals + key cities, expressed as approximate
// (longitude, latitude) → mapped onto our SVG viewBox below. Used for
// pulsing waste-zone markers across the country.
const CITIES = [
  { name: "Trivandrum", x: 215, y: 590, live: true },
  { name: "Bengaluru",  x: 250, y: 530 },
  { name: "Chennai",    x: 295, y: 535 },
  { name: "Hyderabad",  x: 280, y: 460 },
  { name: "Mumbai",     x: 195, y: 410 },
  { name: "Pune",       x: 215, y: 425 },
  { name: "Ahmedabad",  x: 175, y: 345 },
  { name: "Jaipur",     x: 230, y: 290 },
  { name: "Delhi",      x: 260, y: 245 },
  { name: "Lucknow",    x: 300, y: 290 },
  { name: "Patna",      x: 360, y: 320 },
  { name: "Kolkata",    x: 405, y: 360 },
  { name: "Bhubaneswar", x: 365, y: 405 },
  { name: "Guwahati",   x: 460, y: 280 },
  { name: "Bhopal",     x: 245, y: 360 },
  { name: "Chandigarh", x: 245, y: 215 },
  { name: "Srinagar",   x: 215, y: 145 },
  { name: "Goa",        x: 215, y: 470 },
];

// Simplified India outline path (decorative — not cartographically precise).
const INDIA_PATH = "M 220 110 Q 240 95 260 110 L 280 130 L 295 145 Q 310 150 320 165 L 305 190 L 295 215 Q 305 235 320 245 L 350 250 Q 380 245 400 260 L 425 280 L 460 280 L 478 295 L 470 320 Q 480 345 470 365 L 445 380 Q 430 360 415 360 L 395 365 L 380 390 L 365 410 L 345 415 L 335 425 Q 340 450 330 470 L 320 480 Q 305 470 295 480 L 285 510 Q 270 530 255 540 L 235 555 Q 220 575 215 600 L 205 605 Q 195 590 200 570 L 195 545 Q 180 520 180 495 L 170 470 Q 165 445 175 420 L 175 390 Q 165 365 175 340 L 170 310 L 165 280 Q 175 255 195 240 L 205 215 L 200 190 Q 205 165 215 140 Z";

export default function GovernmentPortal() {
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/branding`).then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#050C07] text-white antialiased" data-testid="government-portal">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#050C07]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={branding?.logo_white_url || "/brand/rescigo-logo-white.png"} alt="Rescigo Health" className="h-8 w-auto opacity-95" data-testid="gov-logo" />
          </Link>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#5C9F4F] hidden sm:inline-flex items-center gap-1.5 ml-2">
            <Pulse size={10} weight="fill" /> Government Transparency Portal
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/" className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1.5">
              <ArrowLeft size={11} weight="bold" /> Back to website
            </Link>
            <Link to="/gov/login" data-testid="gov-secure-login"
                  className="h-9 px-3 inline-flex items-center gap-1.5 bg-[#2D5A27] hover:bg-[#3A7033] rounded-md text-xs font-medium">
              <Lock size={11} weight="fill" /> Secure officer login
            </Link>
          </div>
        </div>
      </header>

      <Hero branding={branding} />
      <FeaturesSection branding={branding} />
      <ComplianceSection branding={branding} />
      <CtaSection />
      <Footer branding={branding} />
    </div>
  );
}

const Hero = ({ branding }) => (
  <section className="relative overflow-hidden border-b border-white/5">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(45,90,39,0.4),transparent_60%),radial-gradient(circle_at_75%_60%,rgba(230,57,70,0.18),transparent_55%)]" />
    <div className="absolute inset-0 opacity-30"
         style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.1) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 relative">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E63946]/10 border border-[#E63946]/20 text-[10px] uppercase tracking-[0.22em] text-[#FF8A95] font-medium">
          <ShieldCheck size={11} weight="fill" /> Public · Read-only
        </span>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-medium mt-6 max-w-4xl">
          {branding?.gov_hero_title || (
            <>
              India's <span className="text-[#E63946]">waste flow</span>,<br />
              made <span className="italic font-light text-[#E8F0E7]">transparent</span>.
            </>
          )}
        </h1>
        <p className="text-base sm:text-lg text-slate-300 mt-6 max-w-2xl leading-relaxed">
          {branding?.gov_hero_body || "A live, citizen-facing window into how biomedical and household waste is collected, transported, and disposed across India. Every gram tracked. Every disposal certified."}
        </p>
      </motion.div>
    </div>
  </section>
);

const MapSection = ({ branding }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [active, setActive] = useState(null);
  return (
    <section ref={ref} className="px-6 lg:px-12 py-20 lg:py-24 border-b border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4 order-2 lg:order-1">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
              {branding?.gov_map_title || (<>Live across <span className="text-[#5C9F4F]">India</span>.</>)}
            </h2>
            <p className="text-sm text-slate-400 mt-4 leading-relaxed">
              {branding?.gov_map_body || "Pulse markers represent zones with active Rescigo deployments. The Trivandrum hub is the operational genesis — tap any city to see jurisdictional details once authenticated."}
            </p>
            <div className="mt-7 space-y-2.5">
              {[
                { l: "Active jurisdictions", v: branding?.gov_jurisdictions || "47" },
                { l: "Zones live today", v: branding?.gov_zones || "320+" },
                { l: "Citizens covered", v: branding?.gov_citizens || "1.2M+" },
                { l: "BMW disposal compliance", v: branding?.gov_compliance || "100%" },
              ].map((k) => (
                <div key={k.l} className="flex items-center justify-between py-2.5 border-b border-white/5">
                  <span className="text-xs uppercase tracking-[0.18em] text-slate-500">{k.l}</span>
                  <span className="font-heading text-xl text-[#E8F0E7] tabular-nums">{k.v}</span>
                </div>
              ))}
            </div>
            {active && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                          className="mt-5 rounded-xl bg-white/5 border border-white/10 p-4" data-testid="map-tooltip">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Selected</div>
                <div className="font-heading text-lg mt-1">{active.name}</div>
                <div className="text-xs text-slate-400">
                  {active.live ? "Active deployment · Trivandrum HQ" : "Coverage planning underway"}
                </div>
              </motion.div>
            )}
          </div>

          <div className="lg:col-span-8 order-1 lg:order-2">
            <div className="relative rounded-3xl bg-gradient-to-br from-[#0C170E] via-[#152517] to-[#0C170E] border border-white/10 p-6 sm:p-10 shadow-[0_30px_120px_rgba(45,90,39,0.3)]" data-testid="india-map">
              <svg viewBox="120 80 380 560" className="w-full h-auto" aria-label="India deployment map">
                <defs>
                  <radialGradient id="indiaGrad" cx="50%" cy="40%" r="60%">
                    <stop offset="0%" stopColor="#1F4D1A" />
                    <stop offset="100%" stopColor="#0A1A07" />
                  </radialGradient>
                  <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>

                {/* Outline */}
                <motion.path
                  d={INDIA_PATH}
                  fill="url(#indiaGrad)"
                  stroke="#5C9F4F"
                  strokeWidth="1.4"
                  strokeOpacity="0.7"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={inView ? { pathLength: 1, opacity: 1 } : {}}
                  transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
                />

                {/* Inner mesh */}
                <g opacity="0.18">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <line key={`h${i}`} x1={140} x2={490} y1={120 + i * 60} y2={120 + i * 60}
                          stroke="#5C9F4F" strokeWidth="0.4" strokeDasharray="3 4" />
                  ))}
                  {Array.from({ length: 6 }).map((_, i) => (
                    <line key={`v${i}`} y1={100} y2={620} x1={170 + i * 60} x2={170 + i * 60}
                          stroke="#5C9F4F" strokeWidth="0.4" strokeDasharray="3 4" />
                  ))}
                </g>

                {/* Cities */}
                {CITIES.map((c, i) => (
                  <g key={c.name}
                     onMouseEnter={() => setActive(c)}
                     onMouseLeave={() => setActive(null)}
                     onClick={() => setActive(c)}
                     style={{ cursor: "pointer" }}
                     data-testid={`map-city-${c.name.toLowerCase()}`}>
                    {c.live ? (
                      <>
                        <circle cx={c.x} cy={c.y} r="3" fill="#E63946" filter="url(#glow)">
                          <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite" />
                        </circle>
                        <circle cx={c.x} cy={c.y} r="10" fill="none" stroke="#E63946" strokeOpacity="0.5">
                          <animate attributeName="r" values="3;28;3" dur="3s" repeatCount="indefinite" />
                          <animate attributeName="stroke-opacity" values="0.6;0;0.6" dur="3s" repeatCount="indefinite" />
                        </circle>
                      </>
                    ) : (
                      <motion.circle cx={c.x} cy={c.y} r="2.4" fill="#5C9F4F"
                                     initial={{ opacity: 0 }}
                                     animate={inView ? { opacity: 0.85 } : {}}
                                     transition={{ delay: 1.4 + i * 0.05 }}>
                        <animate attributeName="r" values="2;3.4;2" dur={`${2.5 + (i % 4) * 0.3}s`} repeatCount="indefinite" begin={`${i * 0.18}s`} />
                      </motion.circle>
                    )}
                    <text x={c.x + 7} y={c.y + 3.5}
                          fill={c.live ? "#FF8A95" : "rgba(232,240,231,0.55)"}
                          fontSize="9"
                          fontFamily="ui-monospace, monospace"
                          fontWeight="500"
                          style={{ pointerEvents: "none" }}>
                      {c.name}
                    </text>
                  </g>
                ))}

                {/* Connection lines from Trivandrum to other live deployments */}
                {CITIES.filter(c => !c.live).slice(0, 6).map((c, i) => (
                  <motion.line
                    key={`line-${i}`}
                    x1={215} y1={590} x2={c.x} y2={c.y}
                    stroke="#5C9F4F" strokeWidth="0.6" strokeOpacity="0"
                    strokeDasharray="2 4"
                    initial={{ strokeOpacity: 0 }}
                    animate={inView ? { strokeOpacity: 0.45 } : {}}
                    transition={{ delay: 2.6 + i * 0.15, duration: 0.8 }}
                  />
                ))}
              </svg>
              <div className="absolute bottom-5 right-5 text-[9px] uppercase tracking-[0.22em] text-slate-500 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E63946]" /> Live
                <span className="w-1.5 h-1.5 rounded-full bg-[#5C9F4F] ml-3" /> Coverage planning
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeaturesSection = ({ branding }) => {
  const items = [
    { I: ChartLineUp, t: branding?.gov_feat_1_title || "Read-only dashboards", d: branding?.gov_feat_1_body || "State, district, ward — drill into compliance, complaints, and waste-flow KPIs without touching operational data." },
    { I: MapPin,      t: branding?.gov_feat_2_title || "Heat-maps + waste reports", d: branding?.gov_feat_2_body || "Visualise pickup density, missed-collection clusters, and category-wise volumes by jurisdiction." },
    { I: FileText,    t: branding?.gov_feat_3_title || "Tamper-proof audit trail", d: branding?.gov_feat_3_body || "Every disposal action is logged with timestamp, GPS, and chain-of-custody — discoverable, exportable, court-ready." },
  ];
  return (
    <section className="px-6 lg:px-12 py-20 lg:py-24 border-b border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-medium">{branding?.gov_features_title || "What officials see."}</h2>
          {branding?.gov_features_body && <p className="text-slate-400 mt-4 max-w-2xl mx-auto text-sm">{branding.gov_features_body}</p>}
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          {items.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                        className="rounded-3xl bg-white/[0.03] border border-white/10 p-7 hover:border-[#5C9F4F]/40 hover:bg-white/[0.05] transition-all">
              <f.I size={28} color="#5C9F4F" weight="duotone" />
              <div className="font-heading text-xl tracking-tight font-medium mt-5">{f.t}</div>
              <div className="text-sm text-slate-400 leading-relaxed mt-2">{f.d}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ComplianceSection = ({ branding }) => (
  <section className="px-6 lg:px-12 py-20 lg:py-24 border-b border-white/5">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-medium leading-tight">
          {branding?.gov_compliance_title || (<>Built to stand up in <span className="text-[#E63946]">court</span>.</>)}
        </h2>
        <p className="text-slate-400 mt-5 max-w-md text-sm leading-relaxed">
          {branding?.gov_compliance_body || "Every shipment, every weight, every signature — captured at source, immutable, exportable. Designed for state regulators, pollution control boards, and judicial discovery."}
        </p>
        <ul className="mt-7 space-y-3">
          {(branding?.gov_compliance_bullets && branding.gov_compliance_bullets.length
            ? branding.gov_compliance_bullets
            : [
                "ISO 14001 environmental management",
                "CPCB Bio-Medical Waste Rules 2016 aligned",
                "GDPR-aware data handling · IT Act 2000",
                "TLS 1.3 · session-bound · IP-locked",
                "Independent audit-log export",
              ]).map((x, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                <CheckCircle size={15} weight="fill" className="text-[#5C9F4F] mt-0.5 shrink-0" />
                {x}
              </li>
            ))}
        </ul>
      </div>
      <div className="relative">
        <div className="rounded-3xl bg-gradient-to-br from-[#0C170E] via-[#152517] to-[#0C170E] border border-white/10 p-8">
          <Recycle size={42} color="#5C9F4F" weight="duotone" />
          <div className="font-heading text-2xl mt-5">Chain of custody</div>
          <div className="text-sm text-slate-400 mt-2 mb-6">Each waste shipment carries a verifiable digital trail.</div>
          <ol className="space-y-3 text-sm">
            {[
              { t: "00:00", l: "Customer schedules pickup · phone-OTP verified" },
              { t: "00:42", l: "Field staff scans QR · weight + GPS captured" },
              { t: "01:15", l: "Vehicle reaches CBWTF · seal verified at gate" },
              { t: "02:30", l: "Disposal certified · CPCB-compliant report issued" },
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3 pb-3 border-b border-white/5 last:border-0">
                <span className="text-[10px] font-mono text-[#5C9F4F] mt-1 w-10 shrink-0">{step.t}</span>
                <span className="text-slate-300 text-[13px]">{step.l}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  </section>
);

const CtaSection = () => (
  <section className="px-6 lg:px-12 py-20 lg:py-28">
    <div className="max-w-5xl mx-auto text-center">
      <BuildingOffice size={36} color="#5C9F4F" weight="duotone" className="inline-block mb-5" />
      <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-medium leading-tight">
        Onboard your <span className="text-[#E63946]">jurisdiction</span>.
      </h2>
      <p className="text-slate-400 mt-5 max-w-xl mx-auto text-sm leading-relaxed">
        State health departments, LSGD bodies, pollution control boards — request a private demo for your zone.
      </p>
      <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
        <Link to="/#contact" data-testid="gov-cta-onboard"
              className="h-12 px-6 inline-flex items-center gap-2 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-full text-sm font-medium shadow-lg shadow-green-900/30">
          Request demo <ArrowRight size={14} weight="bold" />
        </Link>
        <Link to="/gov/login" data-testid="gov-cta-secure"
              className="h-12 px-6 inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-full text-sm font-medium">
          <Lock size={13} weight="fill" /> Secure officer login
        </Link>
      </div>
    </div>
  </section>
);

const Footer = ({ branding }) => (
  <footer className="px-6 lg:px-12 py-10 border-t border-white/5">
    <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
      <div className="flex items-center gap-3">
        <img src={branding?.logo_white_url || "/brand/rescigo-logo-white.png"} alt="Rescigo Health" className="h-6 w-auto opacity-80" />
        <span>© {new Date().getFullYear()} Rescigo Health</span>
      </div>
      <div className="flex items-center gap-4">
        <Link to="/" className="hover:text-slate-300">Public site</Link>
        <Link to="/gov/login" className="hover:text-slate-300">Officer login</Link>
        <span>ISO 14001 · CPCB · GDPR-aware</span>
      </div>
    </div>
  </footer>
);
